import React, { useState } from 'react';
import { X, Loader2, CheckCircle, XCircle, CreditCard } from 'lucide-react';
import { getApiBookingRouteId, getUserFacingBookingId } from '../utils/userFacingBookingId';
import { bookingSessionHeaders } from '../utils/bookingSessionCorrelation';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

/**
 * ConfirmHoldModal — Admin/Agent action to convert a Hold booking into
 * a Confirmed Ticket.
 *
 * Sprint H1 (Tripjack) + H2 (Riya). Provider-agnostic; sends to the
 * matching endpoint per booking source.
 *
 * Props:
 *   open       bool
 *   onClose    () => void
 *   booking    object (must have id/booking_id, source, total_amount)
 *   onConfirmed?: () => void
 */
export default function ConfirmHoldModal({ open, onClose, booking, onConfirmed }) {
  const [step, setStep] = useState('confirm');  // confirm | submitting | done | error
  const [error, setError] = useState('');
  const [result, setResult] = useState(null);

  if (!open) return null;

  const source = (booking?.source || '').toLowerCase();
  const apiBookingId = getApiBookingRouteId(booking) || booking?.id || booking?.booking_id;
  const displayBookingRef = getUserFacingBookingId(booking);
  const totalAmount = booking?.total_amount || booking?.totalAmount || 0;

  const endpoint = source === 'riya'
    ? `${API}/riya/issue-ticket`
    : `${API}/tripjack/confirm-booking`;

  const submit = async () => {
    setStep('submitting');
    setError('');
    try {
      const resp = await fetch(endpoint, {
        method: 'POST',
        credentials: 'include',
        headers: { 'Content-Type': 'application/json', ...bookingSessionHeaders() },
        body: JSON.stringify({ booking_id: apiBookingId }),
      });
      // Sprint May-2026 · Read response body via .text() THEN JSON.parse so
      // we never trip "Body is disturbed or locked" if a SW/interceptor
      // already touched the stream — and so we can safely surface the raw
      // payload on parse failures (HTML 502/504, plain-text gateway errors).
      const rawText = await resp.text();
      let data = {};
      if (rawText) {
        try {
          data = JSON.parse(rawText);
        } catch (_pe) {
          // Body wasn't JSON — likely a gateway HTML error page. Use the
          // first 200 chars as the surfaced message.
          data = { detail: rawText.slice(0, 200) };
        }
      }
      if (!resp.ok) {
        const d = typeof data?.detail === 'object' && data?.detail !== null ? data.detail : null;
        if (resp.status === 409 && d?.code === 'FARE_CHANGED' && source !== 'riya') {
          const bid = getApiBookingRouteId(booking) || booking?.id || booking?.booking_id;
          const okApply = window.confirm(
            `${d.message || 'Fare changed before ticketing.'}\n\n` +
            `Old: ₹${Number(d.old_fare || 0).toLocaleString('en-IN')} → New: ₹${Number(d.new_fare || 0).toLocaleString('en-IN')}\n\n` +
            `Refresh this hold from the supplier and update the stored total? You will need to tap "Confirm & Issue Ticket" again.`,
          );
          if (okApply && bid) {
            const r2 = await fetch(`${API}/tripjack/reprice/hold-refresh`, {
              method: 'POST',
              credentials: 'include',
              headers: { 'Content-Type': 'application/json', ...bookingSessionHeaders() },
              body: JSON.stringify({
                booking_id: bid,
                acceptance: {
                  old_total: d.old_fare,
                  new_total: d.new_fare,
                  delta: d.delta,
                  source: 'confirm_hold_modal',
                },
              }),
            });
            const d2 = await r2.json().catch(() => ({}));
            if (!r2.ok) {
              const inner = typeof d2?.detail === 'object' ? d2.detail : d2?.detail;
              throw new Error(inner?.message || inner || 'Hold refresh failed');
            }
            setStep('confirm');
            setError('');
            if (onConfirmed) onConfirmed();
            return;
          }
          throw new Error(
            d.message || 'The fare changed before ticketing. Refresh the hold from the booking list or start a new search.',
          );
        }
        const msg = typeof data?.detail === 'string'
          ? data.detail
          : (data?.detail?.message
             || data?.detail?.user_message
             || data?.message
             || `Could not confirm ticket (HTTP ${resp.status})`);
        throw new Error(msg);
      }
      setResult(data);
      setStep('done');
      if (onConfirmed) onConfirmed();
    } catch (e) {
      // Defensive — never display a raw fetch internal error like "Body is
      // disturbed or locked" to the user; rewrite to actionable copy.
      const raw = e?.message || 'Unknown error';
      const friendly = /body is disturbed|already (read|used)|stream already/i.test(raw)
        ? 'We couldn\'t read the response. Please retry — if this keeps happening, refresh the page and try again.'
        : raw;
      setError(friendly);
      setStep('error');
    }
  };

  // Show neutral, customer-friendly text instead of the raw supplier name
  // (Riya / Tripjack). B2B (agent) + B2C surfaces should never expose the
  // GDS / consolidator the airline ticket actually routes through.
  const providerLabel = 'the airline';

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 px-4"
      data-testid="confirm-hold-modal"
    >
      <div className="bg-white rounded-2xl shadow-2xl w-full max-w-md max-h-[90vh] overflow-y-auto">
        <div className="flex items-center justify-between border-b border-gray-200 px-5 py-4">
          <div className="flex items-center gap-2">
            <CreditCard className="w-5 h-5 text-[#659A9B]" />
            <h3 className="text-lg font-bold text-gray-800">Confirm Hold Ticket</h3>
          </div>
          <button onClick={onClose} className="text-gray-400 hover:text-gray-700" aria-label="Close">
            <X className="w-5 h-5" />
          </button>
        </div>

        <div className="p-5 space-y-4">
          {step === 'confirm' && (
            <>
              <div className="bg-amber-50 border border-amber-200 rounded-lg p-3">
                <p className="text-sm font-semibold text-amber-900">You're about to ticket this Hold booking.</p>
                <p className="text-xs text-amber-800 mt-1">
                  This will issue the airline ticket via {providerLabel}. Once issued, fare and seat are locked.
                  {(booking?.payment_method === 'wallet' && booking?.user_type === 'agent') &&
                    ` ₹${totalAmount.toLocaleString('en-IN')} will be debited from your wallet.`}
                </p>
              </div>
              <div className="bg-gray-50 rounded-lg p-3 text-sm">
                <p><span className="text-gray-600">Booking ID:</span> <span className="font-mono font-semibold">{displayBookingRef}</span></p>
                <p><span className="text-gray-600">Total amount:</span> <strong>₹{(totalAmount).toLocaleString('en-IN')}</strong></p>
                <p><span className="text-gray-600">Provider:</span> {providerLabel}</p>
              </div>
              <div className="flex gap-3 pt-2">
                <button
                  onClick={onClose}
                  className="flex-1 px-4 py-2.5 border border-gray-300 rounded-lg text-gray-700 hover:bg-gray-50 font-medium"
                  data-testid="confirm-hold-back"
                >
                  Cancel
                </button>
                <button
                  onClick={submit}
                  className="flex-1 px-4 py-2.5 bg-[#659A9B] text-white rounded-lg hover:bg-[#5a8b8c] font-medium"
                  data-testid="confirm-hold-submit"
                >
                  Confirm & Issue Ticket
                </button>
              </div>
            </>
          )}

          {step === 'submitting' && (
            <div className="text-center py-6">
              <Loader2 className="w-10 h-10 text-[#659A9B] animate-spin mx-auto mb-3" />
              <p className="text-sm font-semibold text-gray-800">Issuing ticket via {providerLabel}…</p>
              <p className="text-xs text-gray-500 mt-1">This usually takes 5-15 seconds.</p>
            </div>
          )}

          {step === 'done' && (
            <div className="text-center py-4">
              <CheckCircle className="w-12 h-12 text-emerald-500 mx-auto mb-3" />
              <p className="text-base font-semibold text-gray-800">Ticket Issued</p>
              {result?.already_confirmed && (
                <p className="text-xs text-gray-500 mt-1">This booking was already confirmed.</p>
              )}
              {result?.bookingId && (
                <p className="text-xs text-gray-600 mt-2">
                  Booking ID: <span className="font-mono font-semibold">{getUserFacingBookingId(result)}</span>
                </p>
              )}
              {result?.riya_pnr && (
                <p className="text-xs text-gray-600">
                  Riya PNR: <span className="font-mono font-semibold">{result.riya_pnr}</span>
                </p>
              )}
              <button
                onClick={onClose}
                className="mt-4 w-full px-4 py-2.5 bg-emerald-600 text-white rounded-lg hover:bg-emerald-700 font-medium"
                data-testid="confirm-hold-done"
              >
                Close
              </button>
            </div>
          )}

          {step === 'error' && (
            <div className="text-center py-4">
              <XCircle className="w-12 h-12 text-red-500 mx-auto mb-3" />
              <p className="text-base font-semibold text-gray-800">Could not issue ticket</p>
              <p className="text-sm text-gray-600 mt-2">{error}</p>
              <p className="text-xs text-gray-400 mt-3">
                If this is a hold-expired error, the seat may have been released by the airline.
                Please re-book the flight or raise a support ticket.
              </p>
              <button
                onClick={onClose}
                className="mt-4 w-full px-4 py-2.5 bg-gray-700 text-white rounded-lg hover:bg-gray-800 font-medium"
              >
                Close
              </button>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
